/**
 * Wizard step 1, Song (W2-14 onboarding-wizard; DESIGN.md §4.3 "(1) SONG", §5.1 step 2, P§2.7).
 *
 * Drop a file or choose one. The song is decoded at once with Web Audio and shows its waveform (with Play),
 * duration, format and Phira's verdict:
 *  - OGG / WAV: "Phira can play this";
 *  - MP3: plays, plus the soft note that OGG or WAV keep timing most exact across players (decoder delay);
 *  - FLAC (and AAC): plays in Phira, RPE cannot open it: an optional "Convert to WAV for RPE";
 *  - M4A / AAC in MP4 / Opus: Phira cannot play it: "Convert to WAV" (16-bit, `encodeWav` in the audio worker) or
 *    "Choose another file"; Next stays disabled until then.
 * Choosing a song also: hashes it (the asset id), installs its PCM in the audio worker (`dsp.setPcm`, keyed by the
 * SHA-256) and starts the tempo / first-beat detection in the background (`draft.analysis`, read by the Beat step),
 * fills in title and artist from the file's tags (else its name) while the user has not typed them, and puts the
 * song-select preview range inside the song.
 *
 * The detection belongs to the decoded song, not to its file: converting to WAV gives the file a new SHA-256 while
 * the detection may still run under the old one. Its result then still goes into the draft (and into the tempo cache
 * under the new SHA-256 too); it is dropped only when the user removed or replaced the song. Its state lives in the
 * wizard session, so it always ends (found / failed) and is still shown after going back to this step.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type DragEvent as ReactDragEvent,
} from 'react';
import type { NewChartDraft, WizardStepProps } from '../../../app/registry/types';
import { readContainerInfo } from '../../../audio/containerInfo';
import type { TempoCandidate } from '../../../core/audio-dsp/types';
import type { AssetId } from '../../../core/model/types';
import type { MediaSniff } from '../../../formats/types';
import { Button, Icon, ProgressBar } from '../../../ui';
import { readAudioTags } from '../audioTags';
import {
  baseName,
  defaultPreviewRange,
  fitPreviewRange,
  formatDuration,
  guessFromFileName,
  MIN_SONG_SEC,
  stem,
} from '../draft';
import {
  convertToWav,
  decodeSong,
  detectTiming,
  installPcm,
  sha256Of,
  sniffFile,
  songVerdict,
  wakeAudio,
  type DecodedSong,
} from '../songAnalysis';
import { Waveform } from '../Waveform';
import { m } from '../wizard.messages';
import s from '../Wizard.module.css';
import {
  useServicesText,
  useSongPlayer,
  useStepActions,
  useWizardSession,
  type DetectionState,
  type WizardSession,
} from '../wizardSession';

const AUDIO_ACCEPT = 'audio/*,.mp3,.ogg,.oga,.wav,.flac,.m4a,.mp4,.aac,.opus,.webm';

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly name: string }
  | { readonly kind: 'error'; readonly message: string };

type Detection = 'idle' | DetectionState;

/** Display name of a sniffed codec. */
function codecLabel(t: (k: string, p?: Readonly<Record<string, string | number>>) => string, codec: string): string {
  switch (codec) {
    case 'mp3':
      return t(m.codecMp3);
    case 'ogg-vorbis':
      return t(m.codecOgg);
    case 'wav':
      return t(m.codecWav);
    case 'flac':
      return t(m.codecFlac);
    case 'aac':
      return t(m.codecAac);
    case 'm4a':
      return t(m.codecM4a);
    case 'opus':
      return t(m.codecOpus);
    default:
      return t(m.codecOther, { codec: codec.toUpperCase() });
  }
}

/** First file of a drop or a picker. */
function firstFile(list: FileList | null): File | null {
  return list !== null && list.length > 0 ? (list[0] ?? null) : null;
}

/** Reads the bytes of a File (Blob.arrayBuffer, with a FileReader fallback for older engines). */
export async function fileBytes(file: Blob): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer());
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(new Uint8Array(reader.result as ArrayBuffer));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('read failed'));
    };
    reader.readAsArrayBuffer(file);
  });
}

/** Stores tempo candidates in the persist analysis cache under `audio` (best effort). */
async function cacheTempo(
  services: WizardStepProps['services'],
  audio: AssetId,
  candidates: readonly TempoCandidate[],
): Promise<void> {
  await services.persist.analysisSet(audio, 'tempo', candidates).catch(() => undefined);
}

/**
 * Installs the song's PCM in the audio worker and runs the background detection (see the module header): the
 * result goes into the draft while the draft holds this decoded song, under whichever file (original or WAV copy).
 * A failed run is tried once more under the draft's current key with the PCM installed again (the worker drops
 * least recently used songs, and a conversion installs the song under a new key meanwhile).
 */
export function startAnalysis(
  services: WizardStepProps['services'],
  session: WizardSession,
  sha: AssetId,
  song: DecodedSong,
): void {
  const token = session.beginDetection(song);
  void (async () => {
    let analysis: NonNullable<NewChartDraft['analysis']>;
    let key = sha;
    try {
      await installPcm(services, key, song);
      analysis = await detectTiming(services, key);
      const now = session.draft().music;
      if (analysis.candidates === null && now !== null && session.holdsSong(song)) {
        key = now.sha256;
        await installPcm(services, key, song);
        analysis = await detectTiming(services, key);
      }
    } catch {
      analysis = { candidates: null, offset: null };
    }
    const music = session.draft().music;
    if (music !== null && session.holdsSong(song)) {
      session.update({ analysis });
      if (music.sha256 !== key && analysis.candidates !== null) {
        await cacheTempo(services, music.sha256, analysis.candidates);
      }
    }
    session.endDetection(token, analysis.candidates === null ? 'failed' : 'done');
  })();
}

export function SongStep(props: WizardStepProps) {
  const { services, draft } = props;
  const { update, setCanContinue } = useStepActions(props);
  const text = useServicesText(services);
  const session = useWizardSession(props);
  const player = useSongPlayer(services);
  const input = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>(() =>
    draft.music !== null && !session.hasSong(draft.music.sha256)
      ? { kind: 'loading', name: draft.music.name }
      : { kind: 'idle' },
  );
  const [dragOver, setDragOver] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [playError, setPlayError] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const music = draft.music;
  const song = music === null ? undefined : session.song(music.sha256);
  const running = useSyncExternalStore(
    session.subscribe,
    () => session.detectionOf(song),
    () => session.detectionOf(song),
  );
  const detection: Detection = running ?? (draft.analysis === null ? 'idle' : 'done');
  const sniff: MediaSniff | undefined =
    music === null ? undefined : (session.sniff(music.sha256) ?? sniffFile(music.bytes, music.name));
  const verdict = sniff === undefined ? null : songVerdict(sniff);
  // The file's own sample rate and channels (UX-072): the decoded buffer has the audio context's rate.
  const header = useMemo(() => (music === null ? null : readContainerInfo(music.bytes)), [music]);
  const ready = music !== null && song !== undefined && (verdict !== 'needsConversion' || music.converted);

  useEffect(() => {
    setCanContinue(ready);
  }, [ready, setCanContinue]);

  /** Decodes and installs a song file (`known`: hash already known, e.g. pre-filled from a drop). */
  const load = useCallback(
    async (bytes: Uint8Array, name: string, known?: AssetId): Promise<void> => {
      try {
        const sn = sniffFile(bytes, name);
        if (sn.kind !== 'audio') {
          setPhase({ kind: 'error', message: text.t(m.errNotAudio, { name }) });
          return;
        }
        let decoded: DecodedSong;
        try {
          decoded = await decodeSong(services, bytes);
        } catch {
          setPhase({ kind: 'error', message: text.t(m.errDecode, { name }) });
          return;
        }
        if (!(decoded.durationSec >= MIN_SONG_SEC)) {
          setPhase({ kind: 'error', message: text.t(m.errTooShort, { min: MIN_SONG_SEC }) });
          return;
        }
        const sha = known ?? (await sha256Of(services, bytes));
        session.putSong(sha, decoded, sn);
        const cur = session.draft();
        const tags = readAudioTags(bytes);
        const guess = guessFromFileName(name);
        const title = tags.title ?? guess.title;
        const artist = tags.artist ?? guess.artist;
        const fillName = cur.name.trim() === '' || cur.name === session.autoName;
        const fillComposer = cur.composer.trim() === '' || cur.composer === session.autoComposer;
        session.setAutoFilled(fillName ? title : null, fillComposer ? artist : null);
        const range = session.previewEdited
          ? fitPreviewRange(cur.previewStart, cur.previewEnd, decoded.durationSec)
          : defaultPreviewRange(decoded.durationSec);
        session.update({
          ...(fillName ? { name: title } : {}),
          ...(fillComposer ? { composer: artist } : {}),
          music: {
            bytes,
            sha256: sha,
            name: baseName(name),
            mime: sn.mime,
            durationSec: decoded.durationSec,
            converted: false,
          },
          previewStart: range.start,
          previewEnd: range.end,
          analysis: null,
        });
        if (mounted.current) setPhase({ kind: 'idle' });
        startAnalysis(services, session, sha, decoded);
      } catch (e) {
        if (mounted.current) {
          setPhase({
            kind: 'error',
            message: text.t(m.errRead, { detail: e instanceof Error ? e.message : String(e) }),
          });
        }
      }
    },
    [services, session, text],
  );

  // A song pre-filled by the Library (dropped file) is decoded like a chosen one.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    prefilled.current = true;
    const mu = session.draft().music;
    if (mu === null || session.hasSong(mu.sha256)) return;
    // Started after the effect: `load` reports its progress through state.
    queueMicrotask(() => {
      void load(mu.bytes, mu.name, mu.sha256);
    });
  }, [load, session]);

  const choose = async (file: File | null): Promise<void> => {
    if (file === null) return;
    player.stop();
    setPhase({ kind: 'loading', name: file.name });
    setConvertError(null);
    try {
      await load(await fileBytes(file), file.name);
    } catch (e) {
      setPhase({ kind: 'error', message: text.t(m.errRead, { detail: e instanceof Error ? e.message : String(e) }) });
    }
  };

  const openPicker = (): void => {
    wakeAudio(services);
    input.current?.click();
  };

  const onDrop = (e: ReactDragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    wakeAudio(services);
    void choose(firstFile(e.dataTransfer.files));
  };

  const convert = async (): Promise<void> => {
    if (music === null || song === undefined || converting) return;
    player.stop();
    setConverting(true);
    setConvertError(null);
    try {
      const wav = await convertToWav(services, song);
      const sha = await sha256Of(services, wav);
      const wavSniff = sniffFile(wav, 'song.wav');
      session.putSong(sha, song, wavSniff);
      session.update({
        music: {
          bytes: wav,
          sha256: sha,
          name: `${stem(music.name)}.wav`,
          mime: 'audio/wav',
          durationSec: song.durationSec,
          converted: true,
        },
      });
      // The Beat step analyses under the new key: the PCM is the same, so only the key moves (with a finished
      // detection's tempo; one still running stores its result under this key itself).
      void installPcm(services, sha, song);
      const found = session.draft().analysis?.candidates;
      if (found !== null && found !== undefined) void cacheTempo(services, sha, found);
    } catch (e) {
      if (mounted.current)
        setConvertError(text.t(m.convertFailed, { detail: e instanceof Error ? e.message : String(e) }));
    } finally {
      if (mounted.current) setConverting(false);
    }
  };

  const remove = (): void => {
    player.stop();
    setPhase({ kind: 'idle' });
    update({ music: null, analysis: null });
  };

  const bestBpm = (() => {
    const c = draft.analysis?.candidates;
    if (c === null || c === undefined || c.length === 0) return null;
    return [...c].sort((a, b) => b.strength - a.strength)[0]?.bpm ?? null;
  })();

  const facts =
    song === undefined || sniff === undefined
      ? null
      : text.t(m.songFacts, {
          duration: formatDuration(song.durationSec),
          codec: codecLabel(text.t, sniff.codec),
          rate: Math.round((header?.sampleRate ?? song.sampleRate) / 100) / 10,
          channels: header?.channels ?? song.channels.length,
        });

  return (
    <div className={s.step}>
      <div
        className={s.dropZone}
        data-over={dragOver ? 'true' : undefined}
        data-compact={music !== null ? 'true' : undefined}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'copy';
          setDragOver(true);
        }}
        onDragLeave={() => {
          setDragOver(false);
        }}
        onDrop={onDrop}
      >
        <Icon name="Music" size={28} />
        <p className={s.dropText}>
          <span>{text.t(m.songDrop)}</span>{' '}
          <Button variant="primary" icon="FolderOpen" onClick={openPicker}>
            {text.t(music === null ? m.chooseFile : m.chooseAnother)}
          </Button>
        </p>
        <p className={s.hint}>{text.t(m.songAccepted)}</p>
        <input
          ref={input}
          type="file"
          accept={AUDIO_ACCEPT}
          className={s.hiddenInput}
          tabIndex={-1}
          aria-hidden="true"
          data-testid="song-input"
          onChange={(e) => {
            const file = firstFile(e.currentTarget.files);
            e.currentTarget.value = '';
            void choose(file);
          }}
        />
      </div>

      {phase.kind === 'loading' && <ProgressBar value={null} label={{ key: m.songLoading }} />}
      {phase.kind === 'error' && (
        <p className={s.error} role="alert">
          <Icon name="CircleX" size={16} />
          <span>{phase.message}</span>
        </p>
      )}

      {music !== null && song !== undefined && sniff !== undefined && (
        <div className={s.songCard}>
          <div className={s.songHeader}>
            <Icon name="FileMusic" size={18} />
            <span className={s.songName}>{music.name}</span>
            <span className={s.songFacts}>{facts}</span>
            <Button
              variant="ghost"
              icon="X"
              size="sm"
              onClick={remove}
              disabledReason={converting ? { key: m.converting } : null}
            >
              {text.t(m.removeSong)}
            </Button>
          </div>
          <div className={s.waveRow}>
            <Button
              icon={player.playing === 'song' ? 'Square' : 'Play'}
              onClick={() => {
                if (player.playing === 'song') {
                  player.stop();
                  return;
                }
                setPlayError(!player.play('song', song, 0));
              }}
            >
              {text.t(player.playing === 'song' ? m.stop : m.play)}
            </Button>
            <Waveform
              song={song}
              label={text.t(m.waveformLabel, { name: music.name })}
              position={player.position}
              active={player.playing === 'song'}
            />
          </div>
          {playError && (
            <p className={s.hint} role="status">
              {text.t(m.playUnavailable)}
            </p>
          )}
          <Verdict
            verdict={verdict}
            converted={music.converted}
            sizeMb={Math.round(music.bytes.length / 100000) / 10}
            converting={converting}
            t={text.t}
            onConvert={() => {
              void convert();
            }}
            onChooseAnother={openPicker}
          />
          {convertError !== null && (
            <p className={s.error} role="alert">
              <Icon name="CircleX" size={16} />
              <span>{convertError}</span>
            </p>
          )}
          {converting && <ProgressBar value={null} label={{ key: m.converting }} />}
          <p className={s.detect} role="status">
            {detection === 'running' && (
              <>
                <Icon name="LoaderCircle" size={14} />
                <span>{text.t(m.detecting)}</span>
              </>
            )}
            {detection === 'done' && (
              <>
                <Icon name="Activity" size={14} />
                <span>
                  {bestBpm === null
                    ? text.t(m.detectedNone)
                    : text.t(m.detected, { bpm: Math.round(bestBpm * 10) / 10 })}
                </span>
              </>
            )}
            {detection === 'failed' && (
              <>
                <Icon name="Info" size={14} />
                <span>{text.t(m.detectFailed)}</span>
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

function Verdict(props: {
  readonly verdict: ReturnType<typeof songVerdict> | null;
  readonly converted: boolean;
  readonly sizeMb: number;
  readonly converting: boolean;
  readonly t: (k: string, p?: Readonly<Record<string, string | number>>) => string;
  readonly onConvert: () => void;
  readonly onChooseAnother: () => void;
}) {
  const { verdict, converted, converting, t } = props;
  if (verdict === null) return null;
  if (converted) {
    return (
      <p className={s.verdict} data-kind="ok">
        <Icon name="CircleCheck" size={16} />
        <span>
          {t(m.verdictOk)} {t(m.converted, { size: props.sizeMb })}
        </span>
      </p>
    );
  }
  switch (verdict) {
    case 'ok':
      return (
        <p className={s.verdict} data-kind="ok">
          <Icon name="CircleCheck" size={16} />
          <span>{t(m.verdictOk)}</span>
        </p>
      );
    case 'mp3':
      return (
        <p className={s.verdict} data-kind="info">
          <Icon name="Info" size={16} />
          <span>{t(m.verdictMp3)}</span>
        </p>
      );
    case 'notForRpe':
      return (
        <div className={s.verdictBlock}>
          <p className={s.verdict} data-kind="info">
            <Icon name="Info" size={16} />
            <span>{t(m.verdictNotForRpe)}</span>
          </p>
          <div className={s.actions}>
            <Button
              icon="FileMusic"
              onClick={props.onConvert}
              disabledReason={converting ? { key: m.converting } : null}
            >
              {t(m.convertForRpe)}
            </Button>
            <span className={s.hint}>{t(m.convertForRpeHint)}</span>
          </div>
        </div>
      );
    case 'needsConversion':
      return (
        <div className={s.verdictBlock}>
          <p className={s.verdict} data-kind="warning" role="alert">
            <Icon name="TriangleAlert" size={16} />
            <span>{t(m.verdictConvert)}</span>
          </p>
          <div className={s.actions}>
            <Button
              variant="primary"
              icon="FileMusic"
              onClick={props.onConvert}
              disabledReason={converting ? { key: m.converting } : null}
            >
              {t(m.convertToWav)}
            </Button>
            <span className={s.hint}>{t(m.convertHint)}</span>
            <Button icon="FolderOpen" onClick={props.onChooseAnother}>
              {t(m.chooseAnother)}
            </Button>
          </div>
        </div>
      );
  }
}
